import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import MathUtil from '@/util/MathUtil';
import TurnoverReportUtil from '@/util/TurnoverReportUtil';
import VivaSettlementFormat from '@/util/VivaSettlementFormat';
import moment from 'moment';

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

class ReportPdfBuilder {

    fmt(num) {
        if (num > 0 || num < 0) {
            return MathUtil.displayTwoDecimals(num);
        }
        return '0';
    }

    round(num) {
        return MathUtil.roundTwoDecimals(num);
    }

    separator() {
        return {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 355, y2: 0, dash: { length: 3, space: 3 }, lineWidth: 0.5 }],
            margin: [0, 5, 0, 5]
        };
    }

    createDocDefinition(content) {
        return {
            pageSize: 'A4',
            pageMargins: [120, 40, 120, 40],
            content: content,
            styles: {
                title: { fontSize: 14, bold: true },
                tableBold: { fontSize: 9, bold: true },
                tableCell: { fontSize: 9 }
            },
            defaultStyle: { fontSize: 10 }
        };
    }

    downloadPdf(content, fileName) {
        const docDefinition = this.createDocDefinition(content);
        pdfMake.createPdf(docDefinition).download(fileName);
    }

    // ========================
    // Shared section builders
    // ========================

    buildReportHeader(locationOrCompany, title, extraLines) {
        const name = locationOrCompany.name;
        const vatNumber = locationOrCompany.vatNumber;

        const content = [
            { text: name, style: 'title', alignment: 'center' },
            { text: 'Org.nr. ' + vatNumber + ' MVA', alignment: 'center', margin: [0, 0, 0, 5] },
            this.separator(),
            { text: title, style: 'title', alignment: 'center', margin: [0, 0, 0, 5] },
        ];

        if (extraLines) {
            for (const line of extraLines) {
                content.push({ text: line });
            }
        }

        content.push(this.separator());
        return content;
    }

    buildTotalSalesFromData(data, vat) {
        // data = single report object with .categories
        const categories = data.categories || [];

        const calcBrutto = (itemTypes, vatRate) => {
            let total = 0;
            for (const cat of categories) {
                if (itemTypes.includes(cat.itemType) && cat.vatRate === vatRate) {
                    total += cat.amountWithVat;
                }
            }
            return this.round(total);
        };

        const calcVat = (brutto, vatRate) => this.round((brutto / (100 + vatRate)) * vatRate);

        const foodTypes = ['DISH', 'ADD_ON'];
        const drinkTypes = ['DRINK', 'DRINK_BUBBLE_TEA'];

        const foodTakeawayBrutto = calcBrutto(foodTypes, vat.takeOut);
        const foodTakeawayVat = calcVat(foodTakeawayBrutto, vat.takeOut);
        const coldDrinksTakeawayBrutto = calcBrutto(drinkTypes, vat.takeOut);
        const coldDrinksTakeawayVat = calcVat(coldDrinksTakeawayBrutto, vat.takeOut);
        const foodDineInBrutto = calcBrutto(foodTypes, vat.dineIn);
        const foodDineInVat = calcVat(foodDineInBrutto, vat.dineIn);
        const coldDrinksDineInBrutto = calcBrutto(drinkTypes, vat.dineIn);
        const coldDrinksDineInVat = calcVat(coldDrinksDineInBrutto, vat.dineIn);
        const alcoholBrutto = calcBrutto(['DRINK_ALCOHOL'], vat.dineIn);
        const alcoholVat = calcVat(alcoholBrutto, vat.dineIn);
        const deliveryBrutto = calcBrutto(['DELIVERY_SERVICE'], vat.dineIn);
        const deliveryVat = calcVat(deliveryBrutto, vat.dineIn);

        const totalVat = foodTakeawayVat + coldDrinksTakeawayVat + foodDineInVat + coldDrinksDineInVat + alcoholVat + deliveryVat;
        const totalBrutto = foodTakeawayBrutto + coldDrinksTakeawayBrutto + foodDineInBrutto + coldDrinksDineInBrutto + alcoholBrutto + deliveryBrutto;
        const totalNetto = totalBrutto - totalVat;

        const tips = data.tips || 0;

        return { foodTakeawayBrutto, foodTakeawayVat, coldDrinksTakeawayBrutto, coldDrinksTakeawayVat,
            foodDineInBrutto, foodDineInVat, coldDrinksDineInBrutto, coldDrinksDineInVat,
            alcoholBrutto, alcoholVat, deliveryBrutto, deliveryVat,
            totalVat, totalBrutto, totalNetto, tips };
    }

    buildTotalSalesTable(sales, vat) {
        const makeRow = (label, vatRate, vatAmt, netto, brutto, bold) => {
            const style = bold ? 'tableBold' : 'tableCell';
            return [
                { text: label, style },
                { text: vatRate, style, alignment: 'right' },
                { text: this.fmt(vatAmt), style, alignment: 'right' },
                { text: this.fmt(netto), style, alignment: 'right' },
                { text: this.fmt(brutto), style, alignment: 'right' }
            ];
        };

        const tableBody = [
            [
                { text: '', style: 'tableBold' },
                { text: 'MVA%', style: 'tableBold', alignment: 'right' },
                { text: 'MVA', style: 'tableBold', alignment: 'right' },
                { text: 'Netto', style: 'tableBold', alignment: 'right' },
                { text: 'Brutto', style: 'tableBold', alignment: 'right' }
            ],
            makeRow('Mat', vat.takeOut + '%', sales.foodTakeawayVat, sales.foodTakeawayBrutto - sales.foodTakeawayVat, sales.foodTakeawayBrutto),
            makeRow('Kalddrikker', vat.takeOut + '%', sales.coldDrinksTakeawayVat, sales.coldDrinksTakeawayBrutto - sales.coldDrinksTakeawayVat, sales.coldDrinksTakeawayBrutto),
            makeRow('Mat', vat.dineIn + '%', sales.foodDineInVat, sales.foodDineInBrutto - sales.foodDineInVat, sales.foodDineInBrutto),
            makeRow('Kalddrikker', vat.dineIn + '%', sales.coldDrinksDineInVat, sales.coldDrinksDineInBrutto - sales.coldDrinksDineInVat, sales.coldDrinksDineInBrutto),
            makeRow('Alkoholdrikker', vat.dineIn + '%', sales.alcoholVat, sales.alcoholBrutto - sales.alcoholVat, sales.alcoholBrutto),
            makeRow('Utkjøringer', vat.dineIn + '%', sales.deliveryVat, sales.deliveryBrutto - sales.deliveryVat, sales.deliveryBrutto),
            makeRow('Total', '', sales.totalVat, sales.totalNetto, sales.totalBrutto, true),
        ];

        if (sales.tips > 0) {
            tableBody.push([
                { text: 'Tips', style: 'tableBold' },
                { text: '', style: 'tableBold' },
                { text: '', style: 'tableBold' },
                { text: '', style: 'tableBold' },
                { text: this.fmt(sales.tips), style: 'tableBold', alignment: 'right' }
            ]);
        }

        return [
            { text: 'Total omsetning', bold: true, margin: [0, 0, 0, 3] },
            {
                table: { headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto'], body: tableBody },
                layout: 'noBorders'
            },
            this.separator()
        ];
    }

    buildCategoriesTable(categories) {
        const tableBody = [
            [
                { text: 'Kategori', style: 'tableBold' },
                { text: 'MVA%', style: 'tableBold', alignment: 'right' },
                { text: 'MVA', style: 'tableBold', alignment: 'right' },
                { text: 'Brutto', style: 'tableBold', alignment: 'right' }
            ]
        ];

        for (const cat of categories) {
            const vatAmount = (cat.amountWithVat / (100 + cat.vatRate)) * cat.vatRate;
            tableBody.push([
                { text: cat.name, style: 'tableCell' },
                { text: cat.vatRate + '%', style: 'tableCell', alignment: 'right' },
                { text: this.fmt(vatAmount), style: 'tableCell', alignment: 'right' },
                { text: this.fmt(cat.amountWithVat), style: 'tableCell', alignment: 'right' }
            ]);
        }

        return [
            { table: { headerRows: 1, widths: ['*', 'auto', 'auto', 'auto'], body: tableBody }, layout: 'noBorders' },
            this.separator()
        ];
    }

    buildPaymentsTable(cashCount, cashAmount, cardCount, cardAmount, bankAxeptCount, bankAxeptAmount, loyaltyCount, loyaltyAmount) {
        const totalCount = cashCount + cardCount + bankAxeptCount + loyaltyCount;
        const totalAmount = cashAmount + cardAmount + bankAxeptAmount + loyaltyAmount;

        const tableBody = [
            [
                { text: 'Betalingsmiddel', style: 'tableBold' },
                { text: 'Antall', style: 'tableBold', alignment: 'right' },
                { text: 'Beløp', style: 'tableBold', alignment: 'right' }
            ],
            [{ text: 'Kontant', bold: true, colSpan: 3 }, {}, {}],
            [{ text: 'Total kontant', style: 'tableCell' }, { text: '' + cashCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(cashAmount), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Kredittkort', bold: true, colSpan: 3 }, {}, {}],
            [{ text: 'Total kredittkort', style: 'tableCell' }, { text: '' + cardCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(cardAmount), style: 'tableCell', alignment: 'right' }],
            [{ text: 'BankAxept', bold: true, colSpan: 3 }, {}, {}],
            [{ text: 'Total BankAxept', style: 'tableCell' }, { text: '' + bankAxeptCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(bankAxeptAmount), style: 'tableCell', alignment: 'right' }],
        ];

        if (loyaltyCount > 0 || loyaltyAmount > 0) {
            tableBody.push([{ text: 'Bonuspoeng', bold: true, colSpan: 3 }, {}, {}]);
            tableBody.push([{ text: 'Total bonuspoeng', style: 'tableCell' }, { text: '' + loyaltyCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(loyaltyAmount), style: 'tableCell', alignment: 'right' }]);
        }

        tableBody.push([
            { text: 'Total salg', style: 'tableBold' },
            { text: '' + totalCount, style: 'tableBold', alignment: 'right' },
            { text: this.fmt(totalAmount), style: 'tableBold', alignment: 'right' }
        ]);

        return [
            { table: { headerRows: 1, widths: ['*', 'auto', 'auto'], body: tableBody }, layout: 'noBorders' },
            this.separator()
        ];
    }

    buildRefundTable(refundCashCount, refundCashAmount, refundCardCount, refundCardAmount, refundBankAxeptCount, refundBankAxeptAmount, refundBonusCount, refundBonusAmount, totalCount, totalAmount, saftNotice) {
        const tableBody = [
            [
                { text: 'Refusjon', style: 'tableBold' },
                { text: 'Antall', style: 'tableBold', alignment: 'right' },
                { text: 'Beløp', style: 'tableBold', alignment: 'right' }
            ]
        ];

        if (refundCashCount > 0) {
            tableBody.push([{ text: 'Kontant', style: 'tableCell' }, { text: '' + refundCashCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(refundCashAmount), style: 'tableCell', alignment: 'right' }]);
        }
        if (refundCardCount > 0) {
            tableBody.push([{ text: 'Kredittkort', style: 'tableCell' }, { text: '' + refundCardCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(refundCardAmount), style: 'tableCell', alignment: 'right' }]);
        }
        if (refundBankAxeptCount > 0) {
            tableBody.push([{ text: 'BankAxept', style: 'tableCell' }, { text: '' + refundBankAxeptCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(refundBankAxeptAmount), style: 'tableCell', alignment: 'right' }]);
        }
        if (refundBonusCount > 0) {
            tableBody.push([{ text: 'Bonuspoeng', style: 'tableCell' }, { text: '' + refundBonusCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(refundBonusAmount), style: 'tableCell', alignment: 'right' }]);
        }

        // The payments paid back, the rows above added up, as «Total salg» counts payments (founder, 2026-10-29);
        // how many returns there were is «Returkvitteringer». totalCount is that number of returns, for a report that
        // has no rows.
        const paidBackCount = refundCashCount + refundCardCount + refundBankAxeptCount + refundBonusCount;
        tableBody.push([
            { text: 'Total refusjon', style: 'tableBold' },
            { text: '' + (paidBackCount > 0 ? paidBackCount : totalCount), style: 'tableBold', alignment: 'right' },
            { text: this.fmt(totalAmount), style: 'tableBold', alignment: 'right' }
        ]);

        const result = [
            { table: { headerRows: 1, widths: ['*', 'auto', 'auto'], body: tableBody }, layout: 'noBorders' },
        ];

        if (saftNotice && totalCount > 0) {
            result.push({ text: 'SAF-T inneholder kun totalbeløp for refusjon. Fordeling per betalingsmiddel er ikke tilgjengelig.', fontSize: 7, italics: true, color: '#6c757d', margin: [0, 2, 0, 0] });
        }

        result.push(this.separator());
        return result;
    }

    buildVariousCounters(data, terminal) {
        if (terminal.terminalType !== 'POS') return [];

        const content = [];

        const countersBody = [
            [{ text: 'Kasseskuffåpninger:', style: 'tableCell' }, { text: (data.cashBoxOpeningsCount || 0) + 'x', style: 'tableCell' }, { text: '', style: 'tableCell' }],
            [{ text: 'Salgskvitteringer:', style: 'tableCell' }, { text: (data.saleReceiptsCount || 0) + 'x', style: 'tableCell' }, { text: '', style: 'tableCell' }],
            [{ text: 'Kopikvitteringer:', style: 'tableCell' }, { text: (data.copyReceiptsCount || 0) + 'x', style: 'tableCell' }, { text: this.fmt(data.copyReceiptsTotalAmount || 0), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Returkvitteringer:', style: 'tableCell' }, { text: (data.refundReceiptsCount || 0) + 'x', style: 'tableCell' }, { text: this.fmt(data.refundReceiptsTotalAmount || 0), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Rabatter:', style: 'tableCell' }, { text: (data.discountsCount || 0) + 'x', style: 'tableCell' }, { text: this.fmt(data.discountsTotalAmount || 0), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Avbrutte salg:', style: 'tableCell' }, { text: (data.uncompletedSalesCount || 0) + 'x', style: 'tableCell' }, { text: this.fmt(data.uncompletedSalesTotalAmount || 0), style: 'tableCell', alignment: 'right' }],
        ];

        content.push({ table: { widths: ['*', 'auto', 'auto'], body: countersBody }, layout: 'noBorders' });
        content.push(this.separator());

        const grandTotalNet = (data.grandTotalSales || 0) - (data.grandTotalRefund || 0);
        const grandBody = [
            [{ text: 'Grand total salg:', style: 'tableCell' }, { text: this.fmt(data.grandTotalSales || 0), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Grand total retur:', style: 'tableCell' }, { text: this.fmt(data.grandTotalRefund || 0), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Grand total netto:', style: 'tableCell' }, { text: this.fmt(grandTotalNet), style: 'tableCell', alignment: 'right' }],
        ];
        content.push({ table: { widths: ['*', 'auto'], body: grandBody }, layout: 'noBorders' });

        if (data.dayStartedCashAmount != null || data.dayEndedCashAmount != null) {
            content.push(this.separator());
            const cashBody = [];

            if (data.dayStartedCashAmount != null) cashBody.push([{ text: 'Kontanter ved dagsstart:', style: 'tableCell' }, { text: this.fmt(data.dayStartedCashAmount), style: 'tableCell', alignment: 'right' }]);
            if (data.dayEndedCashAmount != null) cashBody.push([{ text: 'Kontanter ved dagsslutt:', style: 'tableCell' }, { text: this.fmt(data.dayEndedCashAmount), style: 'tableCell', alignment: 'right' }]);
            if (data.cashWithdrawalAmount != null && data.cashWithdrawalAmount !== 0) cashBody.push([{ text: 'Kontantuttak:', style: 'tableCell' }, { text: this.fmt(data.cashWithdrawalAmount), style: 'tableCell', alignment: 'right' }]);
            if (data.cashWithdrawalReason) cashBody.push([{ text: 'Grunn for uttak:', style: 'tableCell' }, { text: data.cashWithdrawalReason, style: 'tableCell' }]);
            if (data.diff != null && data.diff !== 0) cashBody.push([{ text: 'Avvik:', style: 'tableCell' }, { text: this.fmt(data.diff), style: 'tableCell', alignment: 'right' }]);
            if (data.comment) cashBody.push([{ text: 'Kommentar:', style: 'tableCell' }, { text: data.comment, style: 'tableCell' }]);

            if (cashBody.length > 0) content.push({ table: { widths: ['*', 'auto'], body: cashBody }, layout: 'noBorders' });

            if (data.dayEndedBy) {
                content.push(this.separator());
                content.push({ table: { widths: ['*', 'auto'], body: [[{ text: 'Dagsoppgjør utført av:', style: 'tableCell' }, { text: data.dayEndedBy, style: 'tableCell' }]] }, layout: 'noBorders' });
            }
        }

        return content;
    }

    // Helper to extract payment data from a single report
    extractPayments(r) {
        const n = (v) => v || 0;

        // Detect unclassified refunds: has refund count but no card/cash classification
        const isUnclassifiedRefund = n(r.refundReceiptsCount) > 0 && r.refundCardCount === undefined && r.refundCashCount === undefined;
        const unclassifiedRefundCount = isUnclassifiedRefund ? n(r.refundReceiptsCount) : 0;
        const unclassifiedRefundAmount = isUnclassifiedRefund ? n(r.refundReceiptsTotalAmount) : 0;

        return {
            cashCount: n(r.cashPickupCount) + n(r.cashDineInCount) + n(r.cashDeliveryCount),
            cashAmount: n(r.cashPickupPaymentsTotalAmount) + n(r.cashDineInPaymentsTotalAmount) + n(r.cashDeliveryPaymentsTotalAmount),
            cardCount: n(r.cardPickupCount) + n(r.cardDineInCount) + n(r.cardDeliveryCount) + n(r.vippsPickupCount) + n(r.vippsDineInCount) + n(r.vippsDeliveryCount) + n(r.vippsDeliveryServiceCount),
            cardAmount: n(r.cardPickupPaymentsTotalAmount) + n(r.cardDineInPaymentsTotalAmount) + n(r.cardDeliveryPaymentsTotalAmount) + n(r.vippsPickupPaymentsTotalAmount) + n(r.vippsDineInPaymentsTotalAmount) + n(r.vippsDeliveryPaymentsTotalAmount) + n(r.vippsDeliveryServicePaymentsTotalAmount),
            bankAxeptCount: n(r.bankAxeptPickupCount) + n(r.bankAxeptDineInCount) + n(r.bankAxeptDeliveryCount),
            bankAxeptAmount: n(r.bankAxeptPickupPaymentsTotalAmount) + n(r.bankAxeptDineInPaymentsTotalAmount) + n(r.bankAxeptDeliveryPaymentsTotalAmount),
            loyaltyCount: n(r.loyaltyPointsCount),
            loyaltyAmount: n(r.loyaltyPointsPaymentsTotalAmount),
            refundCashCount: n(r.refundCashCount),
            refundCashAmount: n(r.refundCashTotalAmount),
            refundCardCount: n(r.refundCardCount) + unclassifiedRefundCount,
            refundCardAmount: n(r.refundCardTotalAmount) + unclassifiedRefundAmount,
            refundBankAxeptCount: n(r.refundBankAxeptCount),
            refundBankAxeptAmount: n(r.refundBankAxeptTotalAmount),
            refundBonusCount: n(r.refundBonusCount),
            refundBonusAmount: n(r.refundBonusTotalAmount),
            refundTotalCount: n(r.refundReceiptsCount),
            refundTotalAmount: n(r.refundReceiptsTotalAmount),
        };
    }

    // ========================
    // Z-Report PDF
    // ========================

    buildZReportContent(zReport, location, terminal, vat) {
        const dateTime = moment(zReport.createdTime).format('DD.MM.YYYY HH:mm');

        let terminalLabel;
        if (terminal.terminalType === 'POS') terminalLabel = 'POS-ID: ' + terminal.terminalId;
        else if (terminal.terminalId === 'WEBSHOP') terminalLabel = 'Kassepunkt: ' + terminal.terminalId;
        else terminalLabel = 'KIOSK-ID: ' + terminal.terminalId;

        const extraLines = [];
        extraLines.push('Dato: ' + dateTime);
        if (terminal.systemIdNr) extraLines.push('ID-nr.: ' + terminal.systemIdNr);
        extraLines.push(terminalLabel);
        extraLines.push('Nr.: ' + zReport.nr);

        const sales = this.buildTotalSalesFromData(zReport, vat);
        const payments = this.extractPayments(zReport);

        return [
            ...this.buildReportHeader(location, 'Z-rapport (Dagsoppgjør)', extraLines),
            ...this.buildTotalSalesTable(sales, vat),
            ...this.buildCategoriesTable(zReport.categories || []),
            ...this.buildPaymentsTable(payments.cashCount, payments.cashAmount, payments.cardCount, payments.cardAmount, payments.bankAxeptCount, payments.bankAxeptAmount, payments.loyaltyCount, payments.loyaltyAmount),
            ...this.buildRefundTable(payments.refundCashCount, payments.refundCashAmount, payments.refundCardCount, payments.refundCardAmount, payments.refundBankAxeptCount, payments.refundBankAxeptAmount, payments.refundBonusCount, payments.refundBonusAmount, payments.refundTotalCount, payments.refundTotalAmount),
            ...this.buildVariousCounters(zReport, terminal),
        ];
    }

    buildZReportPdf(zReport, location, terminal, vat, fileName) {
        const content = this.buildZReportContent(zReport, location, terminal, vat);
        this.downloadPdf(content, fileName || 'Z-rapport.pdf');
    }

    buildMultiZReportPdf(reports, location, terminal, vat, fileName) {
        const sorted = [...reports].sort((a, b) => {
            const nrA = (a.cagistahospitality || a).nr || 0;
            const nrB = (b.cagistahospitality || b).nr || 0;
            return nrA - nrB;
        });
        const content = [];
        for (let i = 0; i < sorted.length; i++) {
            const zReport = sorted[i].cagistahospitality || sorted[i];
            if (i > 0) content.push({ text: '', pageBreak: 'before' });
            content.push(...this.buildZReportContent(zReport, location, terminal, vat));
        }
        this.downloadPdf(content, fileName || 'Z-rapporter.pdf');
    }

    // ========================
    // X-Report PDF
    // ========================

    buildXReportPdf(xReportCounters, location, terminal, vat, fileName) {
        let terminalLabel;
        if (terminal.terminalType === 'POS') terminalLabel = 'POS-ID: ' + terminal.terminalId;
        else if (terminal.terminalId === 'WEBSHOP') terminalLabel = 'Kassepunkt: ' + terminal.terminalId;
        else terminalLabel = 'KIOSK-ID: ' + terminal.terminalId;

        const dateTime = moment().format('DD.MM.YYYY HH:mm');
        const extraLines = ['Dato: ' + dateTime];
        if (terminal.systemIdNr) extraLines.push('ID-nr.: ' + terminal.systemIdNr);
        extraLines.push(terminalLabel);

        const sales = this.buildTotalSalesFromData(xReportCounters, vat);
        const payments = this.extractPayments(xReportCounters);

        const content = [
            ...this.buildReportHeader(location, 'X-rapport', extraLines),
            ...this.buildTotalSalesTable(sales, vat),
            ...this.buildCategoriesTable(xReportCounters.categories || []),
            ...this.buildPaymentsTable(payments.cashCount, payments.cashAmount, payments.cardCount, payments.cardAmount, payments.bankAxeptCount, payments.bankAxeptAmount, payments.loyaltyCount, payments.loyaltyAmount),
            ...this.buildRefundTable(payments.refundCashCount, payments.refundCashAmount, payments.refundCardCount, payments.refundCardAmount, payments.refundBankAxeptCount, payments.refundBankAxeptAmount, payments.refundBonusCount, payments.refundBonusAmount, payments.refundTotalCount, payments.refundTotalAmount),
            ...this.buildVariousCounters(xReportCounters, terminal),
        ];

        this.downloadPdf(content, fileName || 'X-rapport.pdf');
    }

    // ========================
    // Turnover Report PDF
    // ========================

    buildTurnoverReportPdf(zReports, location, terminal, vat, fromDate, toDate, saftSource, fileName) {
        // Header
        let subtitle;
        if (terminal.terminalId === 'ALL') {
            subtitle = 'Omsetningsrapport\n(Alle kassene, selvbetjeningskioskene og nettbutikken samlet)';
        } else if (terminal.terminalId === 'WEBSHOP') {
            subtitle = 'Omsetningsrapport for nettbutikken';
        } else {
            subtitle = 'Omsetningsrapport for ' + terminal.terminalType + ' - ' + terminal.deviceName;
        }

        const extraLines = [
            'Fra og med dato: ' + moment(fromDate).format('DD.MM.YYYY'),
            'Til og med dato: ' + moment(toDate).format('DD.MM.YYYY')
        ];

        // Aggregate total sales across all zReports
        const aggregatedCategories = this.aggregateCategories(zReports);
        const aggregatedData = this.aggregateReportData(zReports);
        const sales = this.buildTotalSalesFromAggregated(zReports, vat);
        const payments = this.aggregatePayments(zReports);

        const content = [
            ...this.buildReportHeader(location, subtitle, extraLines),
            ...this.buildTotalSalesTable(sales, vat),
            ...this.buildCategoriesTable(aggregatedCategories),
            ...this.buildPaymentsTable(payments.cashCount, payments.cashAmount, payments.cardCount, payments.cardAmount, payments.bankAxeptCount, payments.bankAxeptAmount, payments.loyaltyCount, payments.loyaltyAmount),
            ...this.buildRefundTable(payments.refundCashCount, payments.refundCashAmount, payments.refundCardCount, payments.refundCardAmount, payments.refundBankAxeptCount, payments.refundBankAxeptAmount, payments.refundBonusCount, payments.refundBonusAmount, payments.refundTotalCount, payments.refundTotalAmount, saftSource),
        ];

        this.downloadPdf(content, fileName || 'Omsetningsrapport.pdf');
    }

    buildTotalSalesFromAggregated(zReports, vat) {
        // Merge all categories from all reports into one flat array
        const allCategories = [];
        for (const report of zReports) {
            const zReport = report.cagistahospitality || report;
            if (zReport.categories) {
                allCategories.push(...zReport.categories);
            }
        }

        const calcBrutto = (itemTypes, vatRate) => {
            let total = 0;
            for (const cat of allCategories) {
                if (itemTypes.includes(cat.itemType) && cat.vatRate === vatRate) {
                    total += cat.amountWithVat;
                }
            }
            return total;
        };

        const calcVat = (brutto, vatRate) => this.round((brutto / (100 + vatRate)) * vatRate);

        const foodTypes = ['DISH', 'ADD_ON'];
        const drinkTypes = ['DRINK', 'DRINK_BUBBLE_TEA'];

        const foodTakeawayBrutto = calcBrutto(foodTypes, vat.takeOut);
        const foodTakeawayVat = calcVat(foodTakeawayBrutto, vat.takeOut);
        const coldDrinksTakeawayBrutto = calcBrutto(drinkTypes, vat.takeOut);
        const coldDrinksTakeawayVat = calcVat(coldDrinksTakeawayBrutto, vat.takeOut);
        const foodDineInBrutto = calcBrutto(foodTypes, vat.dineIn);
        const foodDineInVat = calcVat(foodDineInBrutto, vat.dineIn);
        const coldDrinksDineInBrutto = calcBrutto(drinkTypes, vat.dineIn);
        const coldDrinksDineInVat = calcVat(coldDrinksDineInBrutto, vat.dineIn);
        const alcoholBrutto = calcBrutto(['DRINK_ALCOHOL'], vat.dineIn);
        const alcoholVat = calcVat(alcoholBrutto, vat.dineIn);
        const deliveryBrutto = calcBrutto(['DELIVERY_SERVICE'], vat.dineIn);
        const deliveryVat = calcVat(deliveryBrutto, vat.dineIn);

        const totalVat = foodTakeawayVat + coldDrinksTakeawayVat + foodDineInVat + coldDrinksDineInVat + alcoholVat + deliveryVat;
        const totalBrutto = foodTakeawayBrutto + coldDrinksTakeawayBrutto + foodDineInBrutto + coldDrinksDineInBrutto + alcoholBrutto + deliveryBrutto;
        const totalNetto = totalBrutto - totalVat;

        let tips = 0;
        for (const report of zReports) {
            const zReport = report.cagistahospitality || report;
            tips += zReport.tips || 0;
        }

        return { foodTakeawayBrutto, foodTakeawayVat, coldDrinksTakeawayBrutto, coldDrinksTakeawayVat,
            foodDineInBrutto, foodDineInVat, coldDrinksDineInBrutto, coldDrinksDineInVat,
            alcoholBrutto, alcoholVat, deliveryBrutto, deliveryVat,
            totalVat, totalBrutto, totalNetto, tips: this.round(tips) };
    }

    aggregateCategories(zReports) {
        const totalCategories = [];
        for (const report of zReports) {
            const zReport = report.cagistahospitality || report;
            for (const cat of (zReport.categories || [])) {
                const clone = Object.assign({}, cat);
                const existing = totalCategories.find(c => c.id === clone.id && c.vatRate === clone.vatRate);
                if (existing) {
                    existing.vatAmount = (existing.vatAmount || 0) + (clone.vatAmount || 0);
                    existing.amountWithVat = existing.amountWithVat + clone.amountWithVat;
                } else {
                    totalCategories.push(clone);
                }
            }
        }
        totalCategories.sort((a, b) => a.vatRate - b.vatRate);
        return totalCategories;
    }

    aggregatePayments(zReports) {
        const n = (v) => v || 0;
        let cashCount = 0, cashAmount = 0, cardCount = 0, cardAmount = 0;
        let bankAxeptCount = 0, bankAxeptAmount = 0, loyaltyCount = 0, loyaltyAmount = 0;
        let refundCashCount = 0, refundCashAmount = 0, refundCardCount = 0, refundCardAmount = 0;
        let refundBankAxeptCount = 0, refundBankAxeptAmount = 0, refundBonusCount = 0, refundBonusAmount = 0;
        let refundTotalCount = 0, refundTotalAmount = 0;

        for (const report of zReports) {
            const r = report.cagistahospitality || report;
            const p = this.extractPayments(r);
            cashCount += p.cashCount; cashAmount += p.cashAmount;
            cardCount += p.cardCount; cardAmount += p.cardAmount;
            bankAxeptCount += p.bankAxeptCount; bankAxeptAmount += p.bankAxeptAmount;
            loyaltyCount += p.loyaltyCount; loyaltyAmount += p.loyaltyAmount;
            refundCashCount += p.refundCashCount; refundCashAmount += p.refundCashAmount;
            refundCardCount += p.refundCardCount; refundCardAmount += p.refundCardAmount;
            refundBankAxeptCount += p.refundBankAxeptCount; refundBankAxeptAmount += p.refundBankAxeptAmount;
            refundBonusCount += p.refundBonusCount; refundBonusAmount += p.refundBonusAmount;
            refundTotalCount += p.refundTotalCount; refundTotalAmount += p.refundTotalAmount;
        }

        return { cashCount, cashAmount, cardCount, cardAmount, bankAxeptCount, bankAxeptAmount, loyaltyCount, loyaltyAmount,
            refundCashCount, refundCashAmount, refundCardCount, refundCardAmount, refundBankAxeptCount, refundBankAxeptAmount,
            refundBonusCount, refundBonusAmount, refundTotalCount, refundTotalAmount };
    }

    aggregateReportData(zReports) {
        // Not needed separately - aggregation is handled by specific methods above
        return {};
    }

    // ========================
    // Transactions Report PDF
    // ========================

    buildTransactionsReportPdf(transactionsReport, company, fromDate, toDate, fileName) {
        const n = (v) => v || 0;
        const extraLines = [
            'Fra dato: ' + moment(fromDate).format('DD.MM.YYYY'),
            'Til dato: ' + moment(toDate).format('DD.MM.YYYY')
        ];

        const bankAxeptCount = n(transactionsReport.bankAxeptCount);
        const bankAxeptAmount = n(transactionsReport.bankAxeptPaymentsTotalAmount);
        const nonBankAxeptCount = n(transactionsReport.nonBankAxeptCount);
        const nonBankAxeptAmount = n(transactionsReport.nonBankAxeptPaymentsTotalAmount);
        const totalCardCount = bankAxeptCount + nonBankAxeptCount;
        const totalCardAmount = bankAxeptAmount + nonBankAxeptAmount;

        const refundBankAxeptCount = n(transactionsReport.refundedBankAxeptCount);
        const refundBankAxeptAmount = n(transactionsReport.refundedBankAxeptPaymentsTotalAmount);
        const refundNonBankAxeptCount = n(transactionsReport.refundedNonBankAxeptCount);
        const refundNonBankAxeptAmount = n(transactionsReport.refundedNonBankAxeptPaymentsTotalAmount);
        const totalRefundCount = refundBankAxeptCount + refundNonBankAxeptCount;
        const totalRefundAmount = refundBankAxeptAmount + refundNonBankAxeptAmount;

        // Sales payments table
        const salesBody = [
            [{ text: 'Betalingsmiddel', style: 'tableBold' }, { text: 'Antall', style: 'tableBold', alignment: 'right' }, { text: 'Beløp', style: 'tableBold', alignment: 'right' }],
            [{ text: 'Kort', bold: true, colSpan: 3 }, {}, {}],
            [{ text: 'BankAxept', style: 'tableCell' }, { text: '' + bankAxeptCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(bankAxeptAmount), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Visa/Mastercard/Andre', style: 'tableCell' }, { text: '' + nonBankAxeptCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(nonBankAxeptAmount), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Total kort', style: 'tableBold' }, { text: '' + totalCardCount, style: 'tableBold', alignment: 'right' }, { text: this.fmt(totalCardAmount), style: 'tableBold', alignment: 'right' }],
        ];

        // Refund table
        const refundBody = [
            [{ text: 'Refusjon', style: 'tableBold' }, { text: 'Antall', style: 'tableBold', alignment: 'right' }, { text: 'Beløp', style: 'tableBold', alignment: 'right' }],
            [{ text: 'BankAxept', style: 'tableCell' }, { text: '' + refundBankAxeptCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(refundBankAxeptAmount), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Visa/Mastercard/Andre', style: 'tableCell' }, { text: '' + refundNonBankAxeptCount, style: 'tableCell', alignment: 'right' }, { text: this.fmt(refundNonBankAxeptAmount), style: 'tableCell', alignment: 'right' }],
            [{ text: 'Total tilbakebetalt', style: 'tableBold' }, { text: '' + totalRefundCount, style: 'tableBold', alignment: 'right' }, { text: this.fmt(totalRefundAmount), style: 'tableBold', alignment: 'right' }],
        ];

        const content = [
            ...this.buildReportHeader(company, 'Transaksjoner', extraLines),
            { table: { headerRows: 1, widths: ['*', 'auto', 'auto'], body: salesBody }, layout: 'noBorders' },
            this.separator(),
            { table: { headerRows: 1, widths: ['*', 'auto', 'auto'], body: refundBody }, layout: 'noBorders' },
            this.separator(),
        ];

        this.downloadPdf(content, fileName || 'Transaksjonsrapport.pdf');
    }

    // ========================
    // Alcohol Report PDF
    // ========================

    buildAlcoholReportPdf(zReports, location, vat, fromDate, toDate, fileName) {
        const extraLines = [
            'Fra og med dato: ' + moment(fromDate).format('DD.MM.YYYY'),
            'Til og med dato: ' + moment(toDate).format('DD.MM.YYYY')
        ];

        // Calculate alcohol totals
        let alcoholBrutto = 0;
        for (const report of zReports) {
            const zReport = report.cagistahospitality || report;
            for (const cat of (zReport.categories || [])) {
                if (cat.itemType === 'DRINK_ALCOHOL' && cat.vatRate === vat.dineIn) {
                    alcoholBrutto += cat.amountWithVat;
                }
            }
        }
        alcoholBrutto = this.round(alcoholBrutto);
        const alcoholVat = this.round((alcoholBrutto / (100 + vat.dineIn)) * vat.dineIn);
        const alcoholNetto = alcoholBrutto - alcoholVat;

        const makeRow = (label, vatRate, vatAmt, netto, brutto, bold) => {
            const style = bold ? 'tableBold' : 'tableCell';
            return [
                { text: label, style },
                { text: vatRate, style, alignment: 'right' },
                { text: this.fmt(vatAmt), style, alignment: 'right' },
                { text: this.fmt(netto), style, alignment: 'right' },
                { text: this.fmt(brutto), style, alignment: 'right' }
            ];
        };

        const salesBody = [
            [{ text: '', style: 'tableBold' }, { text: 'MVA%', style: 'tableBold', alignment: 'right' }, { text: 'MVA', style: 'tableBold', alignment: 'right' }, { text: 'Netto', style: 'tableBold', alignment: 'right' }, { text: 'Brutto', style: 'tableBold', alignment: 'right' }],
            makeRow('Alkoholdrikker', vat.dineIn + '%', alcoholVat, alcoholNetto, alcoholBrutto),
            makeRow('Total', '', alcoholVat, alcoholNetto, alcoholBrutto, true),
        ];

        // Alcohol categories
        const alcoholCategories = [];
        for (const report of zReports) {
            const zReport = report.cagistahospitality || report;
            for (const cat of (zReport.categories || [])) {
                if (cat.itemType === 'DRINK_ALCOHOL') {
                    const clone = Object.assign({}, cat);
                    const existing = alcoholCategories.find(c => c.id === clone.id && c.vatRate === clone.vatRate);
                    if (existing) {
                        existing.amountWithVat += clone.amountWithVat;
                    } else {
                        alcoholCategories.push(clone);
                    }
                }
            }
        }

        const catBody = [
            [{ text: 'Alkoholkategori', style: 'tableBold' }, { text: 'MVA%', style: 'tableBold', alignment: 'right' }, { text: 'MVA', style: 'tableBold', alignment: 'right' }, { text: 'Brutto', style: 'tableBold', alignment: 'right' }]
        ];
        for (const cat of alcoholCategories) {
            const catVat = (cat.amountWithVat / (100 + cat.vatRate)) * cat.vatRate;
            catBody.push([
                { text: cat.name, style: 'tableCell' },
                { text: cat.vatRate + '%', style: 'tableCell', alignment: 'right' },
                { text: this.fmt(catVat), style: 'tableCell', alignment: 'right' },
                { text: this.fmt(cat.amountWithVat), style: 'tableCell', alignment: 'right' }
            ]);
        }

        const content = [
            ...this.buildReportHeader(location, 'Alkoholrapport', extraLines),
            { text: 'Total alkoholsalg', bold: true, margin: [0, 0, 0, 3] },
            { table: { headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto'], body: salesBody }, layout: 'noBorders' },
            this.separator(),
            { table: { headerRows: 1, widths: ['*', 'auto', 'auto', 'auto'], body: catBody }, layout: 'noBorders' },
            this.separator(),
        ];

        this.downloadPdf(content, fileName || 'Alkoholrapport.pdf');
    }

    // ========================
    // SMS Report PDF
    // ========================

    buildSmsReportPdf(messages, location, fromDate, toDate, hasMultiLocations, getLocation, fileName) {
        const extraLines = [
            'Fra og med dato: ' + moment(fromDate).format('DD.MM.YYYY'),
            'Til og med dato: ' + moment(toDate).format('DD.MM.YYYY')
        ];

        let totalSmsCount = 0;
        for (const msg of messages) {
            totalSmsCount += msg.smsCount;
        }

        const content = [
            ...this.buildReportHeader(location, 'SMS rapport', extraLines),
            { text: 'Total antall sendt SMS: ' + totalSmsCount, margin: [0, 0, 0, 3] },
            { text: 'Pris per utsendt SMS: 1 NOK' },
            { text: 'SMS kostnad for perioden: ' + totalSmsCount + ' NOK', bold: true, margin: [0, 0, 0, 5] },
            this.separator(),
            { text: '1 SMS inneholder max 160 tegn. Hvis en melding inneholder flere enn 160 tegn, da blir meldingen satt sammen av flere SMS.', fontSize: 8, margin: [0, 0, 0, 5] },
        ];

        // Build table
        const headerRow = [];
        const widths = [];
        if (hasMultiLocations) {
            headerRow.push({ text: 'Avdeling', style: 'tableBold' });
            widths.push('auto');
        }
        headerRow.push({ text: 'Meldingstype', style: 'tableBold' });
        headerRow.push({ text: 'Meldingstekst', style: 'tableBold' });
        headerRow.push({ text: 'Antall SMS', style: 'tableBold', alignment: 'right' });
        headerRow.push({ text: 'Sendt tid', style: 'tableBold' });
        headerRow.push({ text: 'MeldingsID', style: 'tableBold' });
        headerRow.push({ text: 'OrdreID #', style: 'tableBold' });
        widths.push('auto', '*', 'auto', 'auto', 'auto', 'auto');

        const tableBody = [headerRow];

        const getMessageTypeText = (type) => {
            if (type === 'USER_REGISTRATION') return 'Brukerregistrering';
            if (type === 'ORDER_CONFIRMATION') return 'Ordrebekreftelse';
            if (type === 'FOOD_READY') return 'Maten klar melding';
            return '';
        };

        for (const sms of messages) {
            const row = [];
            if (hasMultiLocations) {
                const loc = getLocation(sms.locationId);
                row.push({ text: loc ? loc.name : '', style: 'tableCell' });
            }
            row.push({ text: getMessageTypeText(sms.messageType), style: 'tableCell' });
            row.push({ text: sms.message || '', style: 'tableCell' });
            row.push({ text: '' + sms.smsCount, style: 'tableCell', alignment: 'right' });
            row.push({ text: moment(sms.sentTime).format('DD.MM.YYYY HH:mm:ss'), style: 'tableCell' });
            row.push({ text: sms.messageId || '', style: 'tableCell' });
            row.push({ text: sms.orderId || '', style: 'tableCell' });
            tableBody.push(row);
        }

        content.push({
            table: { headerRows: 1, widths: widths, body: tableBody },
            layout: 'lightHorizontalLines'
        });

        const docDef = this.createDocDefinition(content);
        docDef.pageOrientation = 'landscape';
        pdfMake.createPdf(docDef).download(fileName || 'SMS_Rapport.pdf');
    }

    /**
     * «Regnskapsbilag»: VIVA's card settlement as a bilag — the clearing days and their sums, then every transaction day
     * by day. With `day`, that one clearing day only: the bilag for a payout that covers one day. The amounts are øre
     * from the server, already added up; nothing is summed here.
     */
    buildVivaSettlementPdf(report, location, day, fileName) {
        const F = VivaSettlementFormat;
        const days = day ? report.days.filter(d => d.date === day) : report.days;
        // Days VIVA's own Sales Clearance list shows otherwise: it counts the day in Greek time.
        const moves = F.vivaListMoves(report);
        const from = day || report.from;
        const to = day || report.to;
        // Space, not a dashed line, between the parts of the head (founder, 2026-10-25). The line there was one object
        // used twice, and pdfmake moves a line's points to where it is drawn, so both landed across the days' table.
        const head = (text, right) => ({ text, style: 'tableBold', alignment: right ? 'right' : 'left' });
        const cell = (text, right, bold) => ({ text, style: bold ? 'tableBold' : 'tableCell', alignment: right ? 'right' : 'left' });
        const sums = (d, bold) => [cell(F.krPdf(d.sales), true, bold), cell(F.krPdf(d.refunded), true, bold),
            cell(F.krPdf(d.fees), true, bold), cell(F.krPdf(d.net), true, bold)];

        const content = [
            { text: location.name || F.accountName(report.account), style: 'title' },
            { text: location.vatNumber ? 'Org.nr. ' + location.vatNumber + ' MVA' : '', margin: [0, 0, 0, 14] },
            { text: 'Betalingsoppgjør fra VIVA', style: 'title', margin: [0, 0, 0, 5] },
            { text: 'Oppgjørsdato: ' + F.period(from, to) },
            { text: 'VIVA-konto: ' + F.accountName(report.account) },
            { text: 'Laget: ' + F.dateTime(report.generatedAt), margin: [0, 0, 0, 10] },
            {
                text: 'VIVA setter inn kortbetalingene på VIVA-kontoen den dagen de klareres, med gebyrene trukket fra. '
                    + 'En utbetaling til bankkontoen er summen av Netto for dagene siden forrige utbetaling. '
                    + 'Refusjoner og gebyrer er trekk og står med minus. Beløp i kroner (NOK).',
                fontSize: 8, margin: [0, 0, 0, 8]
            }
        ];
        for (const warning of report.warnings || []) {
            content.push({ text: 'OBS: ' + warning, fontSize: 8, bold: true, margin: [0, 0, 0, 4] });
        }

        const summary = [[head('Oppgjørsdato'), head('Betalinger', true), head('Salg', true), head('Refusjoner', true),
            head('Gebyr', true), head('Netto', true)]];
        for (const d of days) {
            summary.push([cell(F.dayNumeric(d.date) + (moves[d.date] ? ' *' : '')), cell(String(d.payments), true),
                ...sums(d, false)]);
        }
        if (!day) {
            const t = report.totals;
            summary.push([cell('Sum', false, true), cell(String(t.payments), true, true), ...sums(t, true)]);
        }
        content.push({ table: { headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'], body: summary },
            layout: 'lightHorizontalLines' });
        for (const d of days.filter(d => moves[d.date])) {
            content.push({ text: '* ' + F.dayNumeric(d.date) + ': ' + F.vivaListRemark(moves[d.date]), fontSize: 8,
                margin: [0, 4, 0, 0] });
        }

        for (const d of days) {
            content.push({ text: 'Transaksjoner klarert ' + F.dayLong(d.date), style: 'tableBold', margin: [0, 14, 0, 4] });
            if (moves[d.date]) {
                content.push({ text: F.vivaListLong(moves[d.date]).join(' '), fontSize: 8, margin: [0, 0, 0, 4] });
            }
            const lines = [[head('Salgstidspunkt'), head('Ordre'), head('Hvor'), head('Type'), head('Beløp', true),
                head('Gebyr', true), head('Netto', true)]];
            for (const t of d.transactions) {
                const late = F.listedLaterAtViva(t, d.date) ? '\ngjort opp ' + F.clock(t.clearedAt) : '';
                lines.push([cell(F.dateTime(t.time) + late), cell(t.reference || '-'), cell(F.channel(t.channel)),
                    cell(t.kindLabel), cell(F.krPdf(t.amount), true), cell(F.krPdf(t.fee), true), cell(F.krPdf(t.net), true)]);
            }
            lines.push([cell('Sum', false, true), '', '', '', cell(F.krPdf(d.sales + d.refunded), true, true),
                cell(F.krPdf(d.fees), true, true), cell(F.krPdf(d.net), true, true)]);
            content.push({ table: { headerRows: 1, widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'], body: lines },
                layout: 'lightHorizontalLines' });
        }

        content.push({
            text: 'Kilde: VIVA, hentet av CAGISTA. Utbetalingene til bankkontoen står i VIVA under Kontoer > Bankoverføringer.',
            fontSize: 8, margin: [0, 12, 0, 0]
        });

        const docDef = this.createDocDefinition(content);
        docDef.pageMargins = [40, 40, 40, 50];
        docDef.styles.tableBold = { fontSize: 8, bold: true };
        docDef.styles.tableCell = { fontSize: 8 };
        docDef.footer = (currentPage, pageCount) => ({
            text: 'Side ' + currentPage + ' av ' + pageCount, alignment: 'right', fontSize: 8, margin: [40, 15, 40, 0]
        });
        pdfMake.createPdf(docDef).download(fileName);
    }
}

export default new ReportPdfBuilder();
